import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Pagination,
  Spinner,
  Table,
  TextInput,
  Textarea,
} from "flowbite-react";
import { FaArrowsLeftRight } from "react-icons/fa6";
import axios from "../../config/axiosInstance";
import { useQuery, useQueryClient } from "react-query";
import { ADMIN } from "../../config/apiConfig";
import { formatToReal } from "../../utils/utils";
import AutoCompleteInput from "../inputs/Autocomplete";
import EmptyTable from "./EmptyTable";
import {
  MdDeleteOutline,
  MdLocalPrintshop,
  MdOutlineAttachMoney,
} from "react-icons/md";
import { BsFillBarChartFill, BsTrash } from "react-icons/bs";
import DrawerLoading from "../DrawerLoading";
import { useReactToPrint } from "react-to-print";
import { FiExternalLink } from "react-icons/fi";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { HiOutlineDownload, HiOutlineExclamationCircle } from "react-icons/hi";
import { Tooltip } from "react-tooltip";
import { LuFilter, LuFilterX } from "react-icons/lu";
import CurrencyInput from "../inputs/CurrencyInput";
import { useForm, useWatch } from "react-hook-form";

const DATE_FORMAT = "DD/MM/YYYY";

/**
 * AdminPaymentsTable — versão refatorada, com:
 * - CurrencyInput aceitando vírgula e ponto (parse flexível) e exibindo em pt-BR
 * - Alinhamento de colunas (principal e no modal de selecionados)
 * - Layout de filtros/toolbar simplificado
 * - Estados e efeitos organizados
 */
const AdminPaymentsTable = ({ isActive = true }) => {
  const queryClient = useQueryClient();

  // ===================== LISTAGEM / FILTROS =====================
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPriceSum, setTotalPriceSum] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const onPageChange = (page) => setCurrentPage(page);

  const [selectedCustomer, setSelectedCustomer] = useState({
    label: "Todos os clientes",
    value: 0,
  });

  const reportRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef: reportRef });

  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedTypePayments, setSelectedTypePayments] = useState([]);
  const [onlyOverdue, setOnlyOverdue] = useState(false);

  const [openModalPaymentConfirm, setOpenModalPaymentConfirm] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [errorPaymentConfirm, setErrorPaymentConfirm] = useState(false);
  const [errorPaymentDelete, setErrorPaymentDelete] = useState(false);
  const [openModalPaymentDelete, setOpenModalPaymentDelete] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [showFilters, setShowFilters] = useState(true);
  const [renderFilters, setRenderFilters] = useState(false);

  const [errorInvoicesUI, setErrorInvoicesUI] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  useEffect(() => {
    if (showFilters) setRenderFilters(true);
    else {
      const t = setTimeout(() => setRenderFilters(false), 450);
      return () => clearTimeout(t);
    }
  }, [showFilters]);

  // Presets de datas
  const applyPreset = (fnGet) => {
    const [s, e] = fnGet();
    setStartDate(s);
    setEndDate(e);
  };
  const clearFilters = () => {
    setSelectedCustomer({ label: "Todos os clientes", value: 0 });
    setSelectedStatuses([]);
    setSelectedTypePayments([]);
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
    setOnlyOverdue(false);
  };

  // ===================== QUERIES =====================
  const {
    isLoading: isLoadingCustomers,
    error: errorListCustomers,
    data: listCustomers,
  } = useQuery(
    ["listCustomers"],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_CUSTOMERS}?itemsPerPage=-1`
      );
      if (!data?.data?.data) throw new Error("Erro ao buscar clientes");
      let customers = data.data.data.map((customer) => ({
        label:
          customer.typeCustomer === "CPF"
            ? customer.name
            : customer.socialReason,
        value: customer.id,
        company: customer,
      }));
      customers = [{ label: "Todos os clientes", value: 0 }, ...customers];
      return customers;
    },
    { enabled: isActive }
  );

  const {
    isLoading: isLoadingInvoices,
    data: invoices,
    refetch: refetchInvoices,
  } = useQuery(
    [
      "invoices",
      startDate,
      endDate,
      selectedStatuses,
      selectedTypePayments,
      currentPage,
      selectedCustomer,
    ],
    async () => {
      setErrorInvoicesUI(false);
      const { data } = await axios.get(
        `${ADMIN.GET_PAYMENTS}?startDate=${startDate}&endDate=${endDate}&status=${selectedStatuses}&typePayment=${selectedTypePayments}&page=${currentPage}&companyId=${selectedCustomer?.value}`
      );
      setTotalPages(data.data.totalPages);
      setTotalCount(data.data.totalCount);
      setTotalPriceSum(data.data.totalPrice);
      return data.data.data;
    },
    { enabled: isActive, onError: () => setErrorInvoicesUI(true) }
  );

  // ===================== UTILS =====================
  const handleCheckboxChange = (value, setState) => {
    setState((prev) =>
      prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value]
    );
  };
  const coercePaid = (isPaid) =>
    isPaid === true || isPaid === 1 || isPaid === "1";

  // ===================== AÇÕES (ATUALIZAR / EXCLUIR) =====================
  const handleUpdatePayment = async (type) => {
    setErrorPaymentConfirm(false);
    try {
      await axios.get(
        `${ADMIN.UPDATE_PAYMENT}/${selectedInvoice?.id}?companyId=${
          type === "CONFIRM_UPDATE" ? selectedInvoice?.companyId : ""
        }`
      );
      setOpenModalPaymentConfirm(false);
      queryClient.invalidateQueries(["invoices"]);
    } catch (e) {
      setErrorPaymentConfirm(true);
    }
  };

  const handleDeletePayment = async () => {
    setErrorPaymentDelete(false);
    try {
      await axios.delete(`${ADMIN.DELETE_PAYMENT}/${selectedInvoice?.id}`);
      setOpenModalPaymentDelete(false);
      queryClient.invalidateQueries(["invoices"]);
    } catch (e) {
      setErrorPaymentDelete(true);
    }
  };

  const handleDownloadAttachment = async (invoice) => {
    const attachmentId =
      invoice?.attachmentFileId || invoice?.attachmentFile?.id;
    const companyId = invoice?.companyId || invoice?.Company?.id;
    if (!attachmentId || !companyId) return;
    setDownloadError("");
    try {
      const { data } = await axios.get(
        `${ADMIN.COMPANY_FILES_BASE}/${companyId}/files/${attachmentId}`
      );
      const url = data?.url || data?.data?.url;
      if (!url) throw new Error("URL ausente");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setDownloadError("Não foi possível baixar o anexo. Tente novamente.");
    }
  };

  // ===================== PAGAMENTO MANUAL =====================
  const [openManualModal, setOpenManualModal] = useState(false);
  const [manualDueDate, setManualDueDate] = useState("");
  const [manualDescription, setManualDescription] = useState("");
  const [manualError, setManualError] = useState("");
  const [manualSuccess, setManualSuccess] = useState("");
  const [manualLoading, setManualLoading] = useState(false);
  const [selectedManualCompanies, setSelectedManualCompanies] = useState([]); // [{ companyId, name, value }]
  const [manualAttachment, setManualAttachment] = useState(null);
  const [manualAttachmentKey, setManualAttachmentKey] = useState(0);

  const {
    control: modalControl,
    setValue,
    getValues,
    unregister,
    watch,
  } = useForm({
    mode: "onChange",
    defaultValues: {}, // cada empresa vira um campo dinâmico: company_<id>
  });

  // formatar exibição pt-BR
  const formatMoneyDisplay = (val) => {
    if (val === "" || val == null) return "";
    const n = Number(val);
    if (isNaN(n)) return "";
    return n.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };
  // parse flexível (último separador é decimal)
  const parseMoneyFlexible = (input) => {
    if (input == null) return "";
    let s =
      typeof input === "string"
        ? input
        : typeof input === "number"
        ? String(input)
        : input?.target?.value ?? "";
    s = s.replace(/[^\d.,-]/g, "");
    const lastComma = s.lastIndexOf(",");
    const lastDot = s.lastIndexOf(".");
    const sepIndex = Math.max(lastComma, lastDot);
    let intPart,
      fracPart = "";
    if (sepIndex >= 0) {
      intPart = s.slice(0, sepIndex).replace(/[^\d-]/g, "");
      fracPart = s.slice(sepIndex + 1).replace(/[^\d]/g, "");
    } else {
      intPart = s.replace(/[^\d-]/g, "");
    }
    if (fracPart.length > 2) fracPart = fracPart.slice(0, 2);
    if (intPart === "" && fracPart === "") return "";
    return fracPart ? `${intPart}.${fracPart}` : intPart;
  };

  const companyOptions = useMemo(() => {
    const arr = (listCustomers || []).filter((c) => c.value !== 0);
    arr.sort((a, b) => {
      const A = (a.label || "").toLowerCase();
      const B = (b.label || "").toLowerCase();
      return A.localeCompare(B);
    });
    return arr;
  }, [listCustomers]);

  const fieldName = (id) => `company_${id}`;
  const selectedCompany = selectedManualCompanies[0] || null;
  const selectedCompanyId = selectedCompany?.companyId ?? null;

  const selectCompany = (c) => {
    setSelectedManualCompanies((prev) => {
      const current = prev[0];
      if (!c || !c.value) {
        if (current) unregister(fieldName(current.companyId));
        return [];
      }
      if (current?.companyId === c.value) return prev;
      if (current) unregister(fieldName(current.companyId));
      setValue(fieldName(c.value), "");
      return [
        {
          companyId: c.value,
          name: c.label,
          company: c.company, // <-- guarda o objeto completo
          value: "",
        },
      ];
    });
  };

  const removeCompany = (companyId) => {
    unregister(fieldName(companyId));
    setSelectedManualCompanies((prev) =>
      prev.filter((c) => c.companyId !== companyId)
    );
  };

  const updateCompanyValue = (companyId, val) => {
    const clean = parseMoneyFlexible(val);
    setSelectedManualCompanies((prev) =>
      prev.map((c) => (c.companyId === companyId ? { ...c, value: clean } : c))
    );
  };

  const companyFieldNames = useMemo(
    () => selectedManualCompanies.map((c) => fieldName(c.companyId)),
    [selectedManualCompanies]
  );

  // observar mudanças para recomputar o total
  useWatch({ control: modalControl, name: companyFieldNames });

  const getRowNumber = (id) => {
    const v = getValues(fieldName(id));
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  };

  const isRowInvalid = (id) => {
    const v = getValues(fieldName(id));
    const n = parseFloat(v);
    return !(v !== "" && !isNaN(n) && n > 0);
  };

  const totalManualValue = useMemo(() => {
    return selectedManualCompanies.reduce(
      (sum, c) => sum + getRowNumber(c.companyId),
      0
    );
  }, [watch(companyFieldNames.join("|")), selectedManualCompanies]);

  const canSubmitManual = useMemo(() => {
    if (!manualDueDate) return false;
    if (selectedManualCompanies.length === 0) return false;
    return selectedManualCompanies.every((c) => !isRowInvalid(c.companyId));
  }, [
    manualDueDate,
    watch(companyFieldNames.join("|")),
    selectedManualCompanies,
  ]);

  const submitManualPayment = async () => {
    setManualError("");
    setManualSuccess("");
    setManualLoading(true);
    try {
      const companiesPayload = selectedManualCompanies.map((c) => ({
        ...c.company, // mantém dados úteis (ex.: asaas_id)
        companyId: c.companyId || c.company?.companyId || c.company?.id,
        id: c.companyId || c.company?.companyId || c.company?.id,
        companyName: c.company?.name || c.company?.socialReason || c.name,
        adjustedTotalPrice: getRowNumber(c.companyId), // número já em 1234.56
      }));

      if (manualAttachment) {
        const formData = new FormData();
        formData.append("dueDate", manualDueDate);
        formData.append("description", manualDescription || "");
        formData.append("companies", JSON.stringify(companiesPayload));
        formData.append("attachment", manualAttachment);
        await axios.post(`${ADMIN.RUN_CLOSING}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await axios.post(`${ADMIN.RUN_CLOSING}`, {
          dueDate: manualDueDate,
          description: manualDescription,
          companies: companiesPayload,
        });
      }

      setManualSuccess("Pagamento criado com sucesso.");
      setManualLoading(false);

      setTimeout(() => {
        setOpenManualModal(false);
        setSelectedManualCompanies([]);
        setManualDescription("");
        setManualDueDate("");
        setManualAttachment(null);
        setManualAttachmentKey((k) => k + 1);
        companyFieldNames.forEach((n) => unregister(n));
        queryClient.invalidateQueries(["invoices"]);
      }, 900);
    } catch (err) {
      setManualLoading(false);
      setManualError(
        err?.response?.data?.message ||
          "Não foi possível criar o pagamento. Tente novamente."
      );
    }
  };

  // ===================== ENHANCEMENTS VISUAIS DA TABELA =====================
  const [qText, setQText] = useState("");
  const [qStatus, setQStatus] = useState("ALL"); // ALL | PENDING | PAID
  const [qType, setQType] = useState("ALL"); // ALL | BOLETO | PIX
  const [dense, setDense] = useState(false);

  const filteredInvoices = useMemo(() => {
    if (!invoices) return [];
    return invoices.filter((inv) => {
      const paid = coercePaid(inv.isPaid);
      const isOverdue =
        !paid &&
        inv?.dueDate &&
        moment(inv.dueDate).startOf("day").isBefore(moment().startOf("day"));
      if (qStatus === "PENDING" && paid) return false;
      if (qStatus === "PAID" && !paid) return false;
      if (qType !== "ALL" && inv.type !== qType) return false;
      if (onlyOverdue && !isOverdue) return false;
      if (qText) {
        const name = inv?.Company?.name || inv?.Company?.socialReason || "";
        return name.toLowerCase().includes(qText.toLowerCase());
      }
      return true;
    });
  }, [invoices, qText, qStatus, qType, onlyOverdue]);

  const appliedFilterSummary = useMemo(() => {
    const items = [];
    if (selectedCustomer?.value && selectedCustomer?.value !== 0) {
      items.push(`Cliente: ${selectedCustomer?.label || "-"}`);
    }
    if (startDate || endDate) {
      const startLabel = startDate ? moment(startDate).format(DATE_FORMAT) : "—";
      const endLabel = endDate ? moment(endDate).format(DATE_FORMAT) : "—";
      items.push(`Período: ${startLabel} a ${endLabel}`);
    }
    if (selectedStatuses.length > 0) {
      const labels = selectedStatuses
        .map((s) => (s === 1 ? "Pago" : "Pendente"))
        .join(", ");
      items.push(`Status: ${labels}`);
    }
    if (selectedTypePayments.length > 0) {
      items.push(`Tipo: ${selectedTypePayments.join(", ")}`);
    }
    if (onlyOverdue) {
      items.push("Atrasado");
    }
    return items;
  }, [
    selectedCustomer?.value,
    selectedCustomer?.label,
    startDate,
    endDate,
    selectedStatuses,
    selectedTypePayments,
    onlyOverdue,
  ]);
  const hasActiveFilters = appliedFilterSummary.length > 0;

  const rowPadding = dense ? "py-1.5" : "py-2.5";
  const cellPadding = dense ? "py-1.5" : "py-2";

  const TypeBadge = ({ type }) => {
    const base = "px-2 py-0.5 rounded text-[11px] font-semibold";
    if (type === "PIX")
      return (
        <span className={`${base} bg-emerald-100 text-emerald-700`}>PIX</span>
      );
    if (type === "BOLETO")
      return (
        <span className={`${base} bg-indigo-100 text-indigo-700`}>BOLETO</span>
      );
    return (
      <span className={`${base} bg-gray-100 text-gray-600`}>{type || "-"}</span>
    );
  };

  const StatusBadge = ({ paid }) =>
    paid ? (
      <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-green-100 text-green-700">
        Pago
      </span>
    ) : (
      <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-700">
        Pendente
      </span>
    );

  // ===================== RENDER =====================
  return (
    <div>
      {/* Header Ações */}
      <div className="flex justify-between items-center mb-2 gap-2 flex-wrap">
        <div className="flex gap-2">
          <Button
            size="xs"
            onClick={() => setShowFilters((prev) => !prev)}
            className="primary text-white transition-all duration-300"
          >
            <div className="flex items-center gap-2 transition-all duration-300">
              <span>
                {showFilters ? "Esconder filtros" : "Mostrar filtros"}
              </span>
              <span className="transition-transform duration-300">
                {!showFilters ? <LuFilter /> : <LuFilterX />}
              </span>
            </div>
          </Button>
          <Button size="xs" color="light" onClick={clearFilters}>
            Limpar filtros
          </Button>
        </div>
        <div className="flex gap-2">
          <Button className="bg-gray-500" onClick={reactToPrintFn}>
            <div className="flex gap-2 items-center">
              <div>Imprimir</div>
              <MdLocalPrintshop fontSize={20} />
            </div>
          </Button>
          <Button onClick={() => setOpenManualModal(true)}>
            Gerar boleto manual
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col gap-4">
        {showFilters && (
          <div className="transition-all duration-500 ease-in-out overflow-hidden bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            {renderFilters && (
              <div className="transition-opacity duration-500">
                {isLoadingCustomers && <DrawerLoading />}
                <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-slate-200 bg-gray-50">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Filtros Avançados
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-6 gap-3 px-3 py-3">
                  <div className="flex flex-col md:col-span-2">
                    <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      Cliente
                    </label>
                    <div className="h-9">
                      <AutoCompleteInput
                        options={listCustomers}
                        label=""
                        onChange={(customer) => setSelectedCustomer(customer)}
                        value={selectedCustomer?.value}
                        isLoading={isLoadingCustomers}
                        className="h-full"
                      />
                    </div>
                    {errorListCustomers && (
                      <Alert color="failure" className="mt-2">
                        Ocorreu um erro ao buscar clientes
                      </Alert>
                    )}
                  </div>

                  <div className="flex flex-col md:col-span-3">
                    <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      Período de criação
                    </label>
                    <div className="flex items-center gap-2 h-9">
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          if (moment(e.target.value).isAfter(moment(endDate)))
                            setEndDate(e.target.value);
                        }}
                        className="w-full h-full bg-white border border-slate-300 text-xs rounded-md px-2.5 py-2 focus:ring-slate-500 focus:border-slate-500"
                      />
                      <div className="text-[11px] text-slate-500">até</div>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        min={startDate}
                        className="w-full h-full bg-white border border-slate-300 text-xs rounded-md px-2.5 py-2 focus:ring-slate-500 focus:border-slate-500"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col md:col-span-1">
                    <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      Atrasado
                    </label>
                    <div className="h-9">
                      <button
                        type="button"
                        onClick={() => setOnlyOverdue((v) => !v)}
                        className={`w-full h-full rounded-md text-xs font-semibold border transition ${
                          onlyOverdue
                            ? "bg-rose-600 text-white border-rose-600 shadow-sm"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        Somente
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col md:col-span-3 mt-3">
                    <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      Status
                    </label>
                    <div className="flex gap-2 h-9">
                      <button
                        type="button"
                        onClick={() =>
                          handleCheckboxChange(0, setSelectedStatuses)
                        }
                        className={`h-full px-3 rounded-md text-xs font-semibold border transition w-full ${
                          selectedStatuses.includes(0)
                            ? "bg-amber-600 text-white border-amber-600 shadow-sm"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        Pendente
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleCheckboxChange(1, setSelectedStatuses)
                        }
                        className={`h-full px-3 rounded-md text-xs font-semibold border transition w-full ${
                          selectedStatuses.includes(1)
                            ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        Pago
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col md:col-span-3 mt-3">
                    <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                      Tipo de pagamento
                    </label>
                    <div className="flex gap-2 h-9">
                      <button
                        type="button"
                        onClick={() =>
                          handleCheckboxChange(
                            "BOLETO",
                            setSelectedTypePayments
                          )
                        }
                        className={`h-full px-3 rounded-md text-xs font-semibold border transition w-full ${
                          selectedTypePayments.includes("BOLETO")
                            ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        Boleto
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleCheckboxChange("PIX", setSelectedTypePayments)
                        }
                        className={`h-full px-3 rounded-md text-xs font-semibold border transition w-full ${
                          selectedTypePayments.includes("PIX")
                            ? "bg-emerald-700 text-white border-emerald-700 shadow-sm"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        Pix
                      </button>
                    </div>
                  </div>
                </div>

                {errorInvoicesUI && (
                  <Alert color="failure" className="mt-4">
                    Falha ao carregar os pagamentos.
                    <div className="mt-2">
                      <Button size="xs" color="light" onClick={refetchInvoices}>
                        Tentar novamente
                      </Button>
                    </div>
                  </Alert>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Resumo + Tabela + Paginação */}
      <div className="flex-1 print-padding print-content" ref={reportRef}>
        <div className="mt-3">
          <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-gradient-to-r from-white to-gray-50 px-4 py-2 shadow-sm md:flex-row md:items-center md:justify-between mb-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 shadow-inner">
                  <BsFillBarChartFill className="text-xl" />
                </div>
                <p className="text-base font-semibold text-gray-900">
                  Resultado da busca
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 justify-end text-xs">
              {hasActiveFilters ? (
                <>
                  <span className="uppercase tracking-wide text-gray-500">
                    Filtros ativos
                  </span>
                  {appliedFilterSummary.slice(0, 4).map((item, index) => (
                    <span
                      key={`${item}-${index}`}
                      className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium border border-gray-200 shadow-sm"
                    >
                      {item}
                    </span>
                  ))}
                  {appliedFilterSummary.length > 4 && (
                    <span className="px-2 py-1 rounded-full bg-gray-50 text-gray-500 text-xs font-medium border border-dashed border-gray-300">
                      +{appliedFilterSummary.length - 4} oculto(s)
                    </span>
                  )}
                </>
              ) : (
                <span className="uppercase tracking-wide text-gray-500">
                  Nenhum filtro aplicado
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Cards resumo */}
        <div className="overflow-x-auto">
          <div className="flex gap-4 pb-3 min-w-[500px]">
            <div className="min-w-[200px] flex-1 rounded-2xl p-4 shadow-md text-white bg-gradient-to-br from-[#1E429F] to-[#3B82F6] relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Valor Total</p>
                  <h2 className="text-2xl font-bold mt-1">
                    {!isLoadingInvoices ? (
                      formatToReal(totalPriceSum)
                    ) : (
                      <div className="animate-pulse h-6 bg-white/30 rounded w-28" />
                    )}
                  </h2>
                </div>
                <MdOutlineAttachMoney className="text-white text-4xl opacity-20" />
              </div>
            </div>
            <div className="min-w-[200px] flex-1 rounded-2xl p-4 shadow-md text-white bg-gradient-to-br from-[#0F766E] to-[#14B8A6] relative">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Total de Pagamentos</p>
                  <h2 className="text-2xl font-bold mt-1">
                    {!isLoadingInvoices ? (
                      totalCount
                    ) : (
                      <div className="animate-pulse h-6 bg-white/30 rounded w-24" />
                    )}
                  </h2>
                </div>
                <BsFillBarChartFill className="text-white text-4xl opacity-20" />
              </div>
            </div>
          </div>
        </div>

        {/* Botão imprimir mobile */}
        <div className="mb-4 mt-2 flex md:hidden justify-end">
          <Button className="bg-gray-500" onClick={reactToPrintFn}>
            <div className="flex gap-2 items-center">
              <div>Imprimir</div>
              <MdLocalPrintshop fontSize={20} />
            </div>
          </Button>
        </div>

        <small
          className="gap-2 items-center mt-1 mb-2 flex md:hidden justify-center"
          style={{ fontSize: 10 }}
        >
          <span>Deslize para o lado para visualizar todas as informações</span>{" "}
          <FaArrowsLeftRight />
        </small>

        {/* Tabela principal */}
        {filteredInvoices?.length > 0 || isLoadingInvoices ? (
          <div className="overflow-x-auto shadow-sm rounded-xl mt-0 md:mt-3 border border-gray-200">
            <Table striped style={{ fontSize: "13px" }}>
              <Table.Head>
                <Table.HeadCell className="bg-gray-50/70">
                  Cliente
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-50/70">
                  Criado em
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-50/70">
                  Vencimento
                </Table.HeadCell>
                <Table.HeadCell className="text-right bg-gray-50/70">
                  Valor
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-50/70">
                  Tipo
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-50/70">
                  Status
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-50/70">
                  Ações
                </Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {!isLoadingInvoices ? (
                  <>
                    {filteredInvoices.map((invoice, idx) => {
                      const paid = coercePaid(invoice.isPaid);
                      const createdAt = invoice?.createdAt
                        ? moment(invoice.createdAt)
                        : null;
                      const dueAt = invoice?.dueDate
                        ? moment(invoice.dueDate)
                        : null;
                      const isOverdue =
                        !paid &&
                        dueAt &&
                        dueAt.startOf("day").isBefore(moment().startOf("day"));
                      return (
                        <Table.Row
                          key={idx}
                          className={`bg-white hover:bg-gray-50 transition-colors ${rowPadding} ${
                            isOverdue ? "border-l-4 border-l-rose-500" : ""
                          }`}
                        >
                          <Table.Cell
                            className={`font-medium text-gray-900 ${cellPadding}`}
                          >
                            <div className="leading-tight">
                              <div className="font-semibold">
                                {invoice?.Company?.name ||
                                  invoice?.Company?.socialReason}
                              </div>
                              <small className="block text-xs text-gray-500">
                                {invoice?.Company?.status == 0 ? (
                                  <span className="text-red-500">
                                    Bloqueado
                                  </span>
                                ) : invoice?.Company?.status === -1 ? (
                                  <span className="text-red-500">
                                    Inadimplente
                                  </span>
                                ) : (
                                  <span className="text-green-600">Ativo</span>
                                )}
                              </small>
                            </div>
                          </Table.Cell>
                          <Table.Cell
                            className={`text-center text-gray-900 ${cellPadding}`}
                          >
                            <div className="leading-tight">
                              <div>
                                {createdAt
                                  ? createdAt.format(DATE_FORMAT)
                                  : "-"}
                              </div>
                              {createdAt && (
                                <small className="block text-xs text-gray-500">
                                  {createdAt.fromNow()}
                                </small>
                              )}
                            </div>
                          </Table.Cell>
                          <Table.Cell
                            className={`text-center text-gray-900 ${cellPadding}`}
                          >
                            <div className="leading-tight">
                              <div>
                                {dueAt ? dueAt.format(DATE_FORMAT) : "-"}
                              </div>
                              {dueAt && (
                                <small className="block text-xs text-gray-500">
                                  {dueAt.fromNow()}
                                </small>
                              )}
                            </div>
                          </Table.Cell>
                          <Table.Cell
                            className={`text-right tabular-nums text-gray-900 font-semibold ${cellPadding}`}
                          >
                            {formatToReal(invoice?.price)}
                          </Table.Cell>
                          <Table.Cell className={`text-center ${cellPadding}`}>
                            <TypeBadge type={invoice.type} />
                          </Table.Cell>
                          <Table.Cell className={`text-center ${cellPadding}`}>
                            {isOverdue ? (
                              <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-rose-100 text-rose-700">
                                Atrasado
                              </span>
                            ) : (
                              <StatusBadge paid={paid} />
                            )}
                          </Table.Cell>
                          <Table.Cell className={`text-center ${cellPadding}`}>
                            <div className="flex justify-center gap-2">
                              {(invoice?.attachmentFileId ||
                                invoice?.attachmentFile?.id) && (
                                <div>
                                  <Tooltip
                                    id="tooltip-download-attachment"
                                    content="Baixar anexo"
                                  />
                                  <button
                                    data-tooltip-id="tooltip-download-attachment"
                                    className="inline-block text-center cursor-pointer"
                                    onClick={() =>
                                      handleDownloadAttachment(invoice)
                                    }
                                  >
                                    <HiOutlineDownload
                                      className="text-slate-600"
                                      fontSize={18}
                                    />
                                  </button>
                                </div>
                              )}
                              {invoice.paymentLink && (
                                <div>
                                  <Tooltip
                                    id="tooltip-payment-link"
                                    content="Link de pagamento"
                                  />
                                  <a
                                    data-tooltip-id="tooltip-payment-link"
                                    className="inline-block text-center"
                                    href={invoice.paymentLink}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <FiExternalLink fontSize={18} />
                                  </a>
                                </div>
                              )}
                              {invoice.type === "BOLETO" && !paid && (
                                <div>
                                  <Tooltip
                                    id="tooltip-mark-paid"
                                    content="Sinalizar como pago"
                                  />
                                  <button
                                    data-tooltip-id="tooltip-mark-paid"
                                    className="inline-block text-center cursor-pointer"
                                    onClick={() => {
                                      setSelectedInvoice(invoice);
                                      setOpenModalPaymentConfirm(true);
                                    }}
                                  >
                                    <IoMdCheckmarkCircleOutline
                                      className="text-green-500"
                                      fontSize={18}
                                    />
                                  </button>
                                </div>
                              )}
                              {!paid && (
                                <div>
                                  <Tooltip
                                    id="tooltip-delete-payment"
                                    content="Excluir cobrança"
                                  />
                                  <button
                                    data-tooltip-id="tooltip-delete-payment"
                                    className="inline-block text-center cursor-pointer"
                                    onClick={() => {
                                      setSelectedInvoice(invoice);
                                      setOpenModalPaymentDelete(true);
                                    }}
                                  >
                                    <MdDeleteOutline
                                      className="text-red-500"
                                      fontSize={18}
                                    />
                                  </button>
                                </div>
                              )}
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </>
                ) : (
                  <>
                    {Array.from({ length: 10 }).map((_, index) => (
                      <Table.Row
                        key={index}
                        className={`bg-white ${rowPadding}`}
                      >
                        {Array.from({ length: 7 }).map((__, i) => (
                          <Table.Cell key={i} className="text-center">
                            <div
                              role="status"
                              className="max-w-sm animate-pulse inline"
                            >
                              <div className="h-3 bg-gray-200 rounded-full w-20 mx-auto"></div>
                            </div>
                          </Table.Cell>
                        ))}
                      </Table.Row>
                    ))}
                  </>
                )}
              </Table.Body>
            </Table>
          </div>
        ) : (
          <EmptyTable icon="search_off">
            Nenhum registro encontrado com esses filtros
          </EmptyTable>
        )}

        {downloadError && (
          <Alert color="failure" className="mt-3">
            {downloadError}
          </Alert>
        )}

        {totalPages > 1 && (
          <div className="flex overflow-x-auto justify-center mt-3">
            <Pagination
              layout="pagination"
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              previousLabel=""
              nextLabel=""
              showIcons
            />
          </div>
        )}
      </div>

      {/* Modal Confirmar Pagamento */}
      <Modal
        show={openModalPaymentConfirm}
        size="lg"
        onClose={() => setOpenModalPaymentConfirm(false)}
        popup
      >
        <ModalHeader />
        <ModalBody>
          <div className="text-center">
            <h3 className="mb-2 text-lg font-normal text-gray-600">
              Deseja sinalizar este boleto como pago?
            </h3>
            <p className="text-sm text-gray-500">
              Para dar baixa e desbloquear o cliente, clique no botão{" "}
              <b>"Confirmar e Desbloquear"</b>
            </p>
            <div className="flex flex-col md:flex-row justify-center gap-2 mt-5">
              <Button
                color="success"
                onClick={() => handleUpdatePayment("CONFIRM")}
              >
                Apenas confirmar
              </Button>
              <Button
                color="gray"
                onClick={() => handleUpdatePayment("CONFIRM_UPDATE")}
              >
                Confirmar e desbloquear
              </Button>
            </div>
            {errorPaymentConfirm && (
              <Alert className="mt-4" color="failure" withBorderAccent>
                Ops! Ocorreu algum erro, tente novamente mais tarde!
              </Alert>
            )}
          </div>
        </ModalBody>
      </Modal>

      {/* Modal Excluir Pagamento */}
      <Modal
        show={openModalPaymentDelete}
        size="md"
        onClose={() => setOpenModalPaymentDelete(false)}
        popup
      >
        <ModalHeader />
        <ModalBody>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400" />
            <h3 className="mb-5 text-lg font-normal text-gray-600">
              Tem certeza que deseja excluir essa cobrança?
            </h3>
            <div className="flex justify-center gap-2">
              <Button color="failure" onClick={handleDeletePayment}>
                Excluir
              </Button>
              <Button
                color="gray"
                onClick={() => setOpenModalPaymentDelete(false)}
              >
                Cancelar
              </Button>
            </div>
            {errorPaymentDelete && (
              <Alert className="mt-4" color="failure" withBorderAccent>
                Ops! Ocorreu algum erro, tente novamente mais tarde!
              </Alert>
            )}
          </div>
        </ModalBody>
      </Modal>

      {/* Modal Pagamento Manual */}
      <Modal
        show={openManualModal}
        size="5xl"
        onClose={() => {
          setOpenManualModal(false);
          setManualError("");
          setManualSuccess("");
          setManualAttachment(null);
          setManualAttachmentKey((k) => k + 1);
        }}
        popup
      >
        <ModalHeader />
        <ModalBody>
          <div className="space-y-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h3 className="text-xl font-semibold">
                  Criar pagamento manual
                </h3>
                <p className="text-sm text-gray-500">
                  Gere boleto manual selecionando uma empresa e definindo o
                  valor.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="col-span-1">
                <Label>Data de vencimento</Label>
                <input
                  type="date"
                  value={manualDueDate}
                  onChange={(e) => setManualDueDate(e.target.value)}
                  className="w-full block py-2 px-2 rounded-md text-sm text-gray-700 bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-200"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Descrição (opcional)</Label>
                <Textarea
                  rows={1}
                  value={manualDescription}
                  onChange={(e) => setManualDescription(e.target.value)}
                  placeholder="Ex.: Cobrança complementar referente a ..."
                />
              </div>
            </div>

            {/* Empresa, valor e anexo */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 space-y-4">
                <div className="shadow-sm rounded-md p-3 border border-gray-200">
                  <AutoCompleteInput
                    options={companyOptions}
                    label="Empresa"
                    onChange={(company) => selectCompany(company)}
                    value={selectedCompanyId}
                    isLoading={isLoadingCustomers}
                  />
                  {errorListCustomers && (
                    <Alert color="failure" className="mt-2">
                      Ocorreu um erro ao buscar empresas
                    </Alert>
                  )}

                  {selectedCompany ? (
                    <div className="mt-3 flex items-center justify-between rounded-md border bg-slate-50 px-3 py-2">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {selectedCompany.name}
                      </div>
                      <Button
                        color="light"
                        size="xs"
                        onClick={() => removeCompany(selectedCompany.companyId)}
                      >
                        Remover
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-3 text-xs text-slate-500">
                      Selecione uma empresa para definir o valor.
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Valor do pagamento</Label>
                    <div className="mt-1">
                      {selectedCompanyId ? (
                        <div className="inline-flex flex-col items-center justify-center w-full">
                          <CurrencyInput
                            control={modalControl}
                            name={fieldName(selectedCompanyId)}
                            required
                            inputMode="decimal"
                            placeholder="0,00"
                            className="w-full"
                          />
                        </div>
                      ) : (
                        <TextInput
                          disabled
                          placeholder="Selecione uma empresa"
                        />
                      )}
                    </div>
                  </div>

                  <div>
                    <Label>Anexo (opcional)</Label>
                    <input
                      key={manualAttachmentKey}
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) =>
                        setManualAttachment(e.target.files?.[0] || null)
                      }
                      className="w-full block py-2 px-3 rounded-md text-sm text-gray-700 bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-200"
                    />
                    {manualAttachment && (
                      <div className="mt-2 flex items-center justify-between text-xs text-slate-600">
                        <span className="truncate">
                          {manualAttachment.name}
                        </span>
                        <Button
                          color="light"
                          size="xs"
                          onClick={() => {
                            setManualAttachment(null);
                            setManualAttachmentKey((k) => k + 1);
                          }}
                        >
                          Remover
                        </Button>
                      </div>
                    )}
                    <div className="text-[11px] text-slate-500 mt-1">
                      PDF, PNG ou JPG.
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="border rounded-md p-3 bg-slate-50">
                  <div className="text-xs text-slate-500">Resumo</div>
                  <div className="mt-1 text-sm text-slate-700">
                    <span className="font-medium text-slate-900">
                      {selectedCompany?.name || "Nenhuma empresa selecionada"}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-slate-500">Total</div>
                  <div className="text-lg font-semibold">
                    {formatToReal(totalManualValue)}
                  </div>
                </div>
              </div>
            </div>

            {manualError && (
              <Alert color="failure" withBorderAccent>
                {manualError}
              </Alert>
            )}

            {/* Rodapé */}
            <div className="sticky bottom-0 bg-white border-t pt-3 mt-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="text-xs text-gray-500">
                  {(!manualDueDate || selectedManualCompanies.length === 0) && (
                    <span>Defina o vencimento e selecione 1 empresa.</span>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    color="gray"
                    onClick={() => setOpenManualModal(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    color="success"
                    disabled={!canSubmitManual || manualLoading}
                    onClick={submitManualPayment}
                  >
                    {manualLoading ? (
                      <div className="flex items-center gap-2">
                        <Spinner size="sm" />
                        <span>Processando…</span>
                      </div>
                    ) : (
                      "Criar pagamento"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default AdminPaymentsTable;
